import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import {
  type IReference,
  ReferenceType,
} from '@framework/engine/schema/expressions/references/IReference.ts';
import {
  type IReferencePath,
  ReferencePathDirection,
} from '@framework/engine/schema/expressions/references/IReferencePath.ts';
import { WidgetType } from '@framework/engine/schema/widgets/Widget.ts';
import { getWidgetName } from '@framework/engine/validator/form-builder/getWidgetName.ts';
import { getMapLayerIntersectionFieldSuffixes } from '../createMapSchema.ts';
import { type IMapWidget } from '../IMapWidget.ts';
import { getIntersectionLayersInfo } from '../layers/getIntersectionLayersInfo.ts';

export function getMapWidgetReferences(
  path: IReferencePath,
  widget: IMapWidget,
): IReference {
  return {
    type: ReferenceType.Widget,
    widgetType: widget.type,
    path,
    id: widget.id,
    code: widget.code,
    label: widget.code,
    field: {
      type: Datatype.Array,
      itemsType: Datatype.Object,
      structure: [
        {
          type: ReferenceType.Widget,
          widgetType: WidgetType.Hidden,
          path: [
            ...path,
            { direction: ReferencePathDirection.Child, code: widget.code },
          ],
          id: `${widget.id}@area`,
          code: `area`,
          label: 'Area della Geometria',
          field: {
            type: Datatype.Number,
          },
        },
      ],
    },
  };
}

/**
 * Espone una reference "area totale interessata" per ogni layer di
 * intersezione abilitato, allineata 1:1 ai campi calcolati generati da
 * `createMapSchema`:
 * - layer con `cadastralPreset` -> campo `<code>@cadastralArea`
 * - layer custom -> campo `<code>@intersections_<layerCode>Area`
 *
 * Se il widget non ha layer di intersezione non viene esposta alcuna
 * reference (coerente con lo schema, che in quel caso non crea i campi area).
 */
export function getMapWidgetSiblingReferences(
  path: IReferencePath,
  widget: IMapWidget,
): IReference[] {
  const fieldName = getWidgetName(widget);

  return getIntersectionLayersInfo(widget).map(({ layer }) => {
    const { intersectionAreaSuffix } =
      getMapLayerIntersectionFieldSuffixes(layer);
    const isCadastral = !!layer.intersection.cadastralPreset;

    return {
      type: ReferenceType.Widget,
      widgetType: WidgetType.Hidden,
      path: [...path],
      id: `${widget.id}${intersectionAreaSuffix}`,
      code: `${fieldName}${intersectionAreaSuffix}`,
      label: isCadastral
        ? 'Totale Area interessata'
        : `${layer.intersection.title ?? layer.name}: Totale Area interessata`,
      field: {
        type: Datatype.Number,
      },
    };
  });
}
